<template>
  <div>
    <label class="mb-2 block text-sm font-medium text-gray-700"> Supporting Document(s) </label>
    <p v-if="error" class="text-error-600 text-[14px]">{{ error }}</p>
    <input
      ref="fileInput"
      type="file"
      multiple
      class="hidden"
      @change="handleChange"
      accept="image/*,.pdf,.doc,.docx,.txt"
    />
    <button
      @click="triggerUpload"
      :disabled="uploadingFiles"
      class="w-full flex items-center gap-3 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <UploadIcon class="h-5 w-5 text-grey-light-500" />
      <span class="text-grey-light-500 text-base text-[16px]">{{
        uploadingFiles ? 'Uploading...' : 'No File Chosen'
      }}</span>
    </button>
    <div class="text-xs text-grey-light-500 mt-2">max size of 1MB</div>

    <div v-if="attachments.length > 0 && !uploadingFiles" class="mt-2">
      <ul class="flex flex-col gap-2 file-list">
        <li
          v-for="(file, index) in attachments"
          :key="file.name"
          class="flex justify-between items-center text-sm text-grey-light-700 px-4 py-2 border border-grey-light-200 rounded-lg"
        >
          <div class="flex flex-col">
            <span class="font-semibold text-grey-light-800 truncate max-w-[350px]">
              {{ file.name }}
            </span>
            <span class="text-xs text-grey-light-500">({{ (file.size / 1024).toFixed(2) }} KB)</span>
          </div>
          <Button variant="ghost" size="sm" type="button" @click="removeFile(index)">
            <X />
          </Button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { CLOUDINARY_NAME, CLOUDINARY_UPLOAD_PRESET } from '@/config';
import { addMetric, createException } from '@/utils/monitoring.utils';
import axios from 'axios';
import { UploadIcon, X } from 'lucide-vue-next';
import { ref } from 'vue';

const emit = defineEmits<{
  (e: 'uploaded', urls: string[]): void;
  (e: 'uploading', isUploading: boolean): void;
}>();

const uploadingFiles = ref(false);
const attachments = ref<File[]>([]);
const uploadedUrls = ref<string[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const error = ref('');

const triggerUpload = () => {
  fileInput.value?.click();
};

const uploadFile = async (file: File) => {
  const cloudName = CLOUDINARY_NAME;
  const uploadPreset = CLOUDINARY_UPLOAD_PRESET;
  const apiEndpoint = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

  const formData = new FormData();
  formData.append('upload_preset', uploadPreset);
  formData.append('file', file);

  try {
    const response = await axios.post(apiEndpoint, formData, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      withCredentials: false,
    });

    return response.data.secure_url as string;
  } catch (err) {
    createException(err);
    addMetric('support_ticket_modal_file_upload', { status: 'error', channel: 'merchant', method: 'uploadFile' });
    return '';
  }
};

const handleChange = async (event: Event) => {
  error.value = '';
  uploadingFiles.value = true;
  emit('uploading', true);
  // Clear previously uploaded URLs in parent to avoid stale attachments if this upload fails
  emit('uploaded', []);
  uploadedUrls.value = [];
  attachments.value = [];

  const input = event.target as HTMLInputElement;
  if (!input.files) {
    uploadingFiles.value = false;
    emit('uploading', false);
    emit('uploaded', []);
    return;
  }

  const files = Array.from(input.files);

  if (files.length > 3) {
    // emit empty to signal parent nothing uploaded
    error.value = 'You can only upload up to 3 files.';
    uploadingFiles.value = false;
    emit('uploading', false);
    emit('uploaded', []);
    return;
  }

  const validFiles = files.filter((file) => file.size <= 1024 * 1024);
  const tooLarge = files.filter((file) => file.size > 1024 * 1024);

  if (tooLarge.length) {
    error.value = `Some files exceed 1MB: ${tooLarge.map((f) => f.name).join(', ')}`;
    // still proceed with valid files but ignore too large
  }

  const results = await Promise.all(validFiles.map((f) => uploadFile(f)));

  if (results.some((r) => !r)) {
    // clear parent state to avoid stale URLs from previous uploads
    error.value = 'Failed to upload one or more files. Please try again.';
    uploadingFiles.value = false;
    emit('uploading', false);
    emit('uploaded', []);
    return;
  }

  attachments.value = validFiles;
  uploadedUrls.value = results as string[];
  uploadingFiles.value = false;
  emit('uploading', false);

  error.value = '';
  emit('uploaded', uploadedUrls.value.slice());
};

const removeFile = (index: number) => {
  attachments.value.splice(index, 1);
  uploadedUrls.value.splice(index, 1);
  emit('uploaded', uploadedUrls.value.slice());
  emit('uploading', false);
  error.value = '';
};

defineExpose({ triggerUpload, removeFile });
</script>
